<template>
  <tr>
    <td class="text-left column-fixed bg-white text-body-2 no-border pa-0f trb">
      <div class="d-inline-flex ma-0 pa-0">
        <div class="align-self-center ma-1 font-weight-bold">
          {{ customer.order }}
        </div>
        <div>
          <a
            class="small text-decoration-underline text-nowrap text-black font-weight-bold"
            @click="showCustomerCover(customer.id)"
          >
            <v-tooltip v-if="parseInt(customer?.append, 10) >= 1" top>
              <template #activator="{ on, attrs }">
                <span class="mt-n1 text-danger" v-bind="attrs" v-on="on">{{
                  removeLeftZeros(customer.id)
                }}</span>
              </template>
              <span> Cliente con documentos pendientes por aplicar </span>
            </v-tooltip>
            <span v-else class="text-decoration-underline">
              {{ removeLeftZeros(customer.id) }}
            </span>
            <br />
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <span class="mt-n1" v-bind="attrs" v-on="on">{{
                  customer?.shortName
                }}</span>
              </template>
              <span> {{ customer?.name }} </span>
            </v-tooltip>
          </a>
        </div>
      </div>
    </td>
    <template v-if="!isShowMore">
      <td class="no-border pa-0">
        <div class="d-flex justify-end align-center text-body-2 w-100">
          <svg-icon
            v-if="customer.status.legalStatus"
            name="hammer"
            label="Cliente en proceso legal"
            size="30"
          />

          <v-tooltip v-if="customer.hasGroup" top>
            <template #activator="{ on, attrs }">
              <v-icon color="#DC3545" v-bind="attrs" size="22" v-on="on">
                mdi-account-group
              </v-icon>
            </template>
            <span>Cliente de grupo</span>
          </v-tooltip>
          <v-tooltip v-if="customer.status.suspected >= 1" top>
            <template #activator="{ on, attrs }">
              <v-icon color="#B71C1C" v-bind="attrs" v-on="on">
                mdi-cancel
              </v-icon>
            </template>
            <span>Cliente bloqueado</span>
          </v-tooltip>
          <v-tooltip v-if="customer.isDeactivated" top>
            <template #activator="{ on, attrs }">
              <v-icon color="black" v-bind="attrs" v-on="on">
                mdi-account-remove
              </v-icon>
            </template>
            <span>Cliente dado de baja</span>
          </v-tooltip>
          <svg-icon
            v-if="customer.status.customerSuspected"
            name="cliente-baja"
            label="Cliente con suspensión temporal"
            size="15"
          />

          <v-icon v-if="customer.status.isNew" color="#F71FFF">
            mdi-account-check
          </v-icon>
          <v-btn icon x-small @click="$emit('showPayments', customer.id)">
            <v-icon
              dense
              :color="customer.iel.paidAmount > 0 ? 'green' : 'red'"
            >
              mdi-currency-usd
            </v-icon>
          </v-btn>
          <a class="ma-0" @click="$emit('showPie', customer.id)">
            <v-icon size="22" color="#B71C1C">mdi-chart-pie</v-icon></a
          >
        </div>
      </td>
      <td class="pa-0">
        <!-- customer images  -->
        <div class="d-flex flex-nowrap justify-center">
          <div class="d-flex d-inline-block">
            <svg-icon
              :name="
                customer.person?.hasSignagePhotos
                  ? 'logo-iusa'
                  : 'logo-iusa-gray'
              "
              class="mr-1"
              size="22"
            />
          </div>
          <v-tooltip v-if="customer.person?.hasImages" bottom>
            <template #activator="{ on, attrs }">
              <a
                v-bind="attrs"
                v-on="on"
                @click="showPhoto(customer.person?.document)"
              >
                <v-icon size="22">mdi-camera</v-icon>
              </a>
            </template>
            <span>Ver fotos del cliente</span>
          </v-tooltip>
          <v-tooltip v-else bottom>
            <template #activator="{ on, attrs }">
              <a v-bind="attrs" v-on="on">
                <v-icon size="22" :color="'#CFD8DC'">mdi-camera</v-icon>
              </a>
            </template>
            <span>No hay fotos del cliente</span>
          </v-tooltip>
          <template v-if="customer.person?.document">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon
                  size="22"
                  v-bind="attrs"
                  color="orange"
                  v-on="on"
                  @click="updateCustomerImages(customer)"
                >
                  mdi-upload
                </v-icon>
              </template>
              <span>Actualizar fotos</span>
            </v-tooltip>
            <v-tooltip
              v-if="
                requireUpdatePhotos(
                  customer.person?.document?.last_photo_update
                )
              "
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-icon size="15" v-bind="attrs" color="error" v-on="on">
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>Debe actualizar fotos</span>
            </v-tooltip>
          </template>
        </div>
        <!-- customer images -->
      </td>
      <!--  seniority  -->
      <td class="pa-1">
        <span class="text-caption">
          {{ calcYearsAndMonthsFromMonth(customer.seniorityMonths).text }}
        </span>
      </td>
      <td class="font-weight-bold pa-1 text-nowrap">
        {{ formatFloat(customer.ppdp3, 0) }} ({{
          formatFloat(customer.dppp3, 0)
        }})
      </td>
      <!-- Credit limit  -->
      <td class="pa-0">
        {{ formatNumber(customer.credit.limit, '$', 0) }}
      </td>
      <!-- Available credit  -->
      <td class="pa-0">
        <b> {{ formatNumber(customer.credit.available, '$', 0) }}</b>
      </td>
      <!-- Promissory  -->
      <td class="pa-0">
        <div class="d-flex justify-center align-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                size="22"
                :color="customer?.promissory_note?.color ?? 'gray'"
                v-on="on"
              >
                mdi-circle
              </v-icon>
            </template>
            <span>{{
              customer?.promissory_note?.description ?? 'Sin información'
            }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <a
                v-if="customer?.person?.document?.promissory_note"
                :href="
                  fileServerUrl +
                  '/' +
                  customer?.person?.document?.promissory_note
                "
                target="_blank"
              >
                <v-icon size="22" v-bind="attrs" color="#FF0000" v-on="on"
                  >mdi-file-download</v-icon
                >
              </a>
              <v-icon v-else size="22" v-bind="attrs" color="gray" v-on="on">
                mdi-file-download
              </v-icon>
            </template>
            <span>
              {{
                customer?.person?.document?.promissory_note
                  ? 'Descargar'
                  : 'Sin informacion adicional'
              }}
            </span>
          </v-tooltip>
          <v-tooltip v-if="customer?.promissoryNoteRequests" bottom>
            <template #activator="{ on, attrs }">
              <v-badge
                v-if="customer?.promissoryNoteRequests?.is_rejected"
                color="error"
                overlap
                icon="mdi-alert-octagon"
              >
                <v-icon
                  size="24"
                  color="primary"
                  @click="$emit('updatePromissoryNote', customer)"
                  >mdi-file-cancel</v-icon
                >
              </v-badge>
              <v-btn
                v-else
                color="primary"
                icon
                v-bind="attrs"
                @click="$emit('updatePromissoryNote', customer)"
                v-on="on"
              >
                <v-icon size="22"
                  >{{
                    customer.promissoryNoteRequests?.in_progress
                      ? 'mdi-file-arrow-up-down'
                      : 'mdi-file-document-plus'
                  }}
                </v-icon>
              </v-btn>
            </template>
            <span>Solicitudes de pagaré</span>
          </v-tooltip>
        </div>
      </td>
      <td class="pa-0">
        <b>{{ formatFloat(customer.billingPncPercentage, 0) }}%</b>
      </td>
      <!-- New products (GN) -->
      <td class="pa-0">
        <b>{{ formatFloat(customer.billingGnPercentage, 0) }}%</b>
      </td>
      <!-- cumulative growth   -->
      <td
        class="text-decoration-underline text-yellow pa-0"
        :class="{
          'text-success': customer.cumulativeGrowth > 0,
          'text-danger': customer.cumulativeGrowth < 0,
        }"
      >
        <a @click="$emit('showCumulativeGrowth', customer.id)">
          {{ formatFloat(customer.cumulativeGrowth, 2) }}%</a
        >
      </td>
      <!-- avg billing (last 6 months)-->
      <td class="font-weight-bold pa-0">
        {{ formatNumber(customer?.averagePurchaseLastSixMonths ?? 0, '$', 0) }}
      </td>
      <!-- Orders  -->
      <td>
        <b>{{ formatNumber(customer.orders ?? 0, '$', 0) }}</b>
      </td>
      <!-- Backorders  -->
      <td class="font-weight-bold pa-0 text-white">
        <div class="d-flex d-inline">
          <template v-for="item in backorderStatusList">
            <v-tooltip v-if="item.value > 0" :key="item.key" bottom>
              <template #activator="{ on, attrs }">
                <v-avatar
                  v-bind="attrs"
                  size="22"
                  :class="item.class"
                  v-on="on"
                >
                  <span>{{ formatFloat(item.value, 0) }}</span>
                </v-avatar>
              </template>
              <span>{{ formatNumber(item.amount, '$', 0) }}</span>
            </v-tooltip>
          </template>
        </div>
      </td>
    </template>
    <!-- Quarterly billing  -->
    <template v-if="billingType == 1">
      <td
        v-for="item in customer.history.quarterly.slice(0, isShowMore ? 13 : 4)"
        :key="item.i"
        class="font-weight-bold pa-0"
      >
        <span> {{ formatNumber(item.billing, '$', 0) }}</span>
        <br />
        <span class="text-caption"> {{ item.assortment }}</span>
      </td>
    </template>
    <!-- Monthly billing -->
    <template v-else>
      <!-- avg billing (last 12 months)- -->
      <td v-if="isShowMore" class="font-weight-bold pa-0">
        {{ formatNumber(customer?.AVG12Billing, '$', 0) }} <br />
        {{ formatNumber(customer?.AVG12Reng, '', 0) }}
      </td>
      <td
        v-for="item in customer.history.monthly.slice(0, isShowMore ? 13 : 4)"
        :key="item.i"
        class="font-weight-bold pa-0"
      >
        <span> {{ formatNumber(item.billing, '$', 0) }}</span
        ><br />
        <span class="text-caption">{{ item.assortment }}</span>
      </td>
    </template>
  </tr>
</template>

<script>
  /* eslint-disable vue/require-default-prop, vue/require-prop-types */
  import numbersFormats from '@/mixins/numbersFormats';
  import dates from '@/mixins/dates';
  import moment from 'moment';
  import SvgIcon from '@/components/icons/SvgIcon.vue';

  export default {
    components: { SvgIcon },
    mixins: [numbersFormats, dates],
    props: {
      customer: [Object],
      isShowMore: {
        type: Boolean,
      },
      billingType: {
        type: String,
      },
    },
    data() {
      return {
        fileServerUrl: process.env.VUE_APP_FILES_URL,
      };
    },
    computed: {
      backorderStatusList() {
        const b = this.customer.backorders;
        return [
          {
            key: 'available',
            value: b.availableCount,
            amount: b.available,
            class: 'in-stock-bg',
          },
          {
            key: 'partial',
            value: b.partialCount,
            amount: b.partial,
            class: 'partial-stock-bg',
          },
          {
            key: 'noStock',
            value: b.noStockCount,
            amount: b.noStock,
            class: 'no-stock-bg',
          },
          {
            key: 'programmed',
            value: b.programmedCount,
            amount: b.programmed,
            class: 'programmed-stock-bg',
          },
        ];
      },
    },
    methods: {
      updateCustomerImages(customer) {
        customer.DOCUMENT = {
          modal: true,
          customerId: customer?.person.id,
          userId: customer?.person.userId,
          document: customer.person.document,
          customer: customer,
          last_photo_update: customer?.person?.document?.last_photo_update,
        };
        this.$emit('onUpdateCustomerImages', customer.DOCUMENT);
      },
      showCustomerCover(customer) {
        localStorage.setItem('currentCustomerCode', JSON.stringify(customer));
        this.$router.push({ name: 'CustomerCoverDetails' });
      },
      requireUpdatePhotos(date) {
        const months = this.calculateMonths(date);
        return months > 6 ? true : false; //If it is greater than 6, then remember that it must be updated
      },
      calculateMonths(date) {
        if (date) {
          const currentDate = moment();
          const selectedDate = moment(date);
          const monthsDiff = currentDate.diff(selectedDate, 'months');
          return Math.abs(monthsDiff);
        } else {
          return 0;
        }
      },
      prepareGalery(document) {
        var photos = [];
        const photoMappings = [
          { key: 'facade_photo', description: 'Fachada', title: 'Fachada' },
          {
            key: 'display_photo',
            description: 'Exhibición',
            title: 'Exhibición',
          },
          {
            key: 'left_display_photo',
            description: 'Exhibición Izquierda',
            title: 'Exhibición Izquierda',
          },
          {
            key: 'right_display_photo',
            description: 'Exhibición Derecha',
            title: 'Exhibición Derecha',
          },
          {
            key: 'indoor_photo',
            description: 'Exhibición central',
            title: 'Exhibición central',
          },
          {
            key: 'bar_photo',
            description: 'Mostrador/Bodega',
            title: 'Bodega',
          },
        ];
        photoMappings.forEach((photo) => {
          if (document?.[photo.key]) {
            photos.push({
              description: photo.description,
              title: photo.title,
              src: this.fileServerUrl + '/' + document[photo.key],
            });
          }
        });
        // Signage photos
        for (let i = 1; i <= 3; i++) {
          const signageKey = `storefront_signage_photo${i}`;
          if (document?.[signageKey]) {
            photos.push({
              description: `Rotulado ${i}`,
              title: `Rotulado ${i}`,
              src: this.fileServerUrl + '/' + document[signageKey],
            });
          }
        }
        return photos;
      },
      showPhoto(document) {
        this.$emit('onShowPhoto', this.prepareGalery(document));
      },
    },
  };
</script>
